import { Spinner } from "@components/ui-kit";
import {
  useExpensesTableSummaryLazyQuery,
  useGetExpensesLazyQuery,
} from "@src/__generated__/graphql";
import { useExpenseFilterOptionsQuery } from "@src/__generated__/urql-graphql";
import { onError } from "@src/utils/apollo";
import { withCan } from "@src/utils/components/permissions";
import { useUpdateURI } from "@src/utils/hooks";
import { parseActiveQueryParam } from "@src/utils/routing";
import { getParamFromQuery } from "@src/utils/url";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { CardListExpenses } from "./CardListExpenses";
import { QuickFilters, StoreExpenses } from "./StoreExpenses";

export const PageExpensesCtx = React.createContext<StoreExpenses>(
  null as any as StoreExpenses,
);

export function useData(store: StoreExpenses) {
  const [
    fetchExpensesSummary,
    { loading: loadingSummary, refetch: refetchSummary },
  ] = useExpensesTableSummaryLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (!data) return;
      store.allExpensesIds = data.expensesSummary.expenseIds || [];
      store.expensesSummary = data.expensesSummary;
    },
    ...onError(),
  });

  const [fetchExpenses, { loading, refetch }] = useGetExpensesLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (!data) return;
      store.fromGetExpensesQuery(data);
    },
    ...onError(),
  });

  const [{ data: filterOptions }] = useExpenseFilterOptionsQuery();

  useEffect(() => {
    fetchExpenses({ variables: store.queryParams });
    fetchExpensesSummary({ variables: store.queryParams });
  }, [store.queryParams]);

  useEffect(() => {
    if (!filterOptions) return;
    store.setFilterOptions(filterOptions);
  }, [filterOptions]);

  store.refetch = () => {
    refetch();
    refetchSummary();
  };

  return { loading, loadingSummary };
}

export const PageExpenses = withCan(
  { do: ["expense_read_all", "expense_read_own", "expense_read_team"] },
  observer(function PageExpenses() {
    const { query } = useRouter();

    const [store] = useState(() => {
      const store = StoreExpenses.create();
      store.where.fromURLSearchParam(query);
      store.orderBy.fromURLSearchParam(query);
      store.pagination.fromURLSearchParam(query);
      store.searchTerm = getParamFromQuery("search", query) ?? "";
      if (parseActiveQueryParam(query) === QuickFilters.ExpenseItems) {
        store.setActiveView("expenseItems", false);
      }
      return store;
    });

    const { loading, loadingSummary } = useData(store);

    useUpdateURI(store.searchParams, "active", store.isExpenseItemsView);

    return (
      <PageExpensesCtx.Provider value={store}>
        <div className="flex justify-center items-center">
          <div className="w-full">
            {loading && <Spinner absolute />}
            <CardListExpenses isSummaryLoaded={!loadingSummary} />
          </div>
        </div>
      </PageExpensesCtx.Provider>
    );
  }),
);
